import React, { useState } from 'react';

const TIER_LABELS = {
  0: 'Tier 0: Amino Acids',
  1: 'Tier 1: Precursors',
  2: 'Tier 2: Neurotransmitters',
  3: 'Tier 3: Brain Regions',
};

const TIER_COLORS = {
  0: '#8b5cf6',  // purple
  1: '#3b82f6',  // blue
  2: '#22c55e',  // green
  3: '#f59e0b',  // gold
};

function CascadeVisualizer({ cascade }) {
  const [expandedTier, setExpandedTier] = useState(null);

  if (!cascade) return null;

  const tiers = [
    { tier: 0, data: cascade.tier0_amino_acids, label: TIER_LABELS[0] },
    { tier: 1, data: cascade.tier1_precursors, label: TIER_LABELS[1] },
    { tier: 2, data: cascade.tier2_neurotransmitters, label: TIER_LABELS[2] },
    { tier: 3, data: cascade.tier3_brain_regions, label: TIER_LABELS[3] },
  ];

  const annotationClass = (val) => {
    if (val >= 0.70) return 'elevated';
    if (val <= 0.25) return 'low';
    return 'normal';
  };

  return (
    <div className="cascade-viz">
      {/* Header */}
      <div className="cascade-viz-header">
        <div className="cascade-viz-stat">
          <span className="cascade-stat-val">{cascade.active_heads}</span>
          <span className="cascade-stat-label">/ {cascade.total_heads} Active Heads</span>
        </div>
        <div className="cascade-viz-pathway">
          {cascade.dominant_pathway}
        </div>
      </div>

      {/* Tier Flow Diagram */}
      <div className="cascade-flow">
        {tiers.map(({ tier, data, label }) => {
          const entries = Object.entries(data || {});
          const isExpanded = expandedTier === tier;
          return (
            <div key={tier} className={`cascade-tier ${isExpanded ? 'expanded' : ''}`}>
              <div
                className="cascade-tier-header"
                style={{ borderLeftColor: TIER_COLORS[tier] }}
                onClick={() => setExpandedTier(isExpanded ? null : tier)}
              >
                <span className="cascade-tier-label">{label}</span>
                <span className="cascade-tier-count">{entries.length} heads</span>
                <span className="cascade-tier-toggle">{isExpanded ? '\u25B2' : '\u25BC'}</span>
              </div>

              {/* Compact bar view (always visible) */}
              <div className="cascade-tier-bars">
                {entries.map(([name, val]) => (
                  <div key={name} className="cascade-head-compact">
                    <span className="cascade-head-name">{name.replace(/_/g, ' ')}</span>
                    <div className="cascade-head-bar-bg">
                      <div
                        className={`cascade-head-bar ${annotationClass(val)}`}
                        style={{ width: `${val * 100}%`, backgroundColor: TIER_COLORS[tier] }}
                      />
                    </div>
                    <span className={`cascade-head-val ${annotationClass(val)}`}>{val.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Expanded detail (annotations) */}
              {isExpanded && cascade.heads && (
                <div className="cascade-tier-detail">
                  {entries.map(([name]) => {
                    const head = cascade.heads[name];
                    if (!head) return null;
                    return (
                      <div key={name} className="cascade-head-detail">
                        <span className="cascade-detail-name">{name.replace(/_/g, ' ')}</span>
                        <span className={`cascade-annotation ${head.annotation}`}>{head.annotation}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Flow arrow between tiers */}
              {tier < 3 && (
                <div className="cascade-flow-arrow">
                  <div className="cascade-arrow-line" />
                  <div className="cascade-arrow-head">\u25BC</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CascadeVisualizer;
